import { Checklist, ChecklistCriteria } from "types/Checklist";
import { Paragraphs } from "types/Paragraph";
import { Revision } from "types/Revision";
import { RevisionAnnotations } from "types/RevisionAnnotation";
import { SubmittedDocument } from "types/SubmittedDocument";
import { WordChecklistCells } from "types/WordChecklist";
import { usePopulateAnnotationMaps } from "~/composables/wordGenerator/usePopulateAnnotationMaps";

import { ref as firebaseRef, getBlob } from "firebase/storage";
import { useFirebaseStorage } from "vuefire";
import { usePDFChecklistGenerator } from "~/composables/pdfGenerator/usePDFChecklistGenerator";
import { RevisionCommentPlacement } from "~/types/enums/RevisionCommentPlacement.enum";
import { usePDFParagraphGenerator } from "./usePDFParagraphGenerator";

export const usePDFGenerator = () => {
  const exportPDFDocument = async (
    submittedDocument: SubmittedDocument,
    revision: Revision,
    checklist: Checklist | undefined,
    allCriteria: ChecklistCriteria[] | undefined,
    wordChecklistCells: WordChecklistCells | undefined,
    paragraphs: Paragraphs,
    annotations: RevisionAnnotations,
    totalEarnedScore: number | undefined,
    earnedScore: number | undefined,
    commentColor: number[],
    returnBlob: boolean = false,
    printMode: boolean = false,
    hideScores: boolean = false,
    fileNameOverride: string | undefined = undefined
  ): Promise<Blob | undefined> => {
    if (!revision) {
      throw createError("Revision not found");
    }

    const revisionCommentPlacement =
      revision.commentPlacement ?? RevisionCommentPlacement.AfterDocument;

    let docName = fileNameOverride ?? submittedDocument!.name;
    docName = docName.replace(".docx", "");
    if (fileNameOverride == undefined) {
      docName = `${docName}_graded.pdf`;
    } else {
      docName = `${docName}.pdf`;
    }

    paragraphs = useTrimParagraphs(paragraphs);

    const { annotationMap, annotationParagraphMap, endLineAnnotations } =
      usePopulateAnnotationMaps(annotations);

    const orientation = "p"; // Portrait
    const format = [216, 279]; // Custom format in mm (Width x Height)

    const content = [];

    if (
      checklist &&
      wordChecklistCells &&
      allCriteria &&
      totalEarnedScore != undefined &&
      earnedScore != undefined
    ) {
      const checklistData = usePDFChecklistGenerator(
        submittedDocument,
        checklist,
        wordChecklistCells,
        allCriteria,
        totalEarnedScore,
        earnedScore,
        hideScores
      );

      content.push(...checklistData);
    }

    content.push({ text: "", pageBreak: "before" });

    if (
      revision.commentObject &&
      revisionCommentPlacement == RevisionCommentPlacement.BeforeDocument
    ) {
      // @ts-ignore
      // var lines = nuxtApp.$convertQuillDeltaToLines(revision.commentObject);
      const lines = useConvertQuillDeltaToPDFRun(
        revision.commentObject,
        12,
        useRGBToHex(commentColor)
      );

      content.push({
        text: "\n",
      });

      for (const line of lines) {
        content.push({
          text: line,
        });
      }
    }

    let previousParagraphCollectionRef = undefined;

    for (const paragraph of paragraphs) {
      const paragraphData = usePDFParagraphGenerator(
        paragraph,
        annotationMap,
        annotationParagraphMap,
        endLineAnnotations,
        previousParagraphCollectionRef,
        commentColor,
        false
      );

      previousParagraphCollectionRef = paragraph.collectionRef;

      content.push(paragraphData);
    }

    if (revision.fileObject) {
      const isImage = revision.fileObject.mediaType.includes("image");

      const isPDF = revision.fileObject.mediaType.includes("pdf");

      if (isPDF) {
        let lines: any[] = [];

        if (revision.commentObject) {
          // @ts-ignore
          // var lines = nuxtApp.$convertQuillDeltaToLines(revision.commentObject);
          lines = useConvertQuillDeltaToPDFRun(
            revision.commentObject,
            12,
            useRGBToHex(commentColor)
          );
        }

        const response: any = await $fetch(
          `/api/documents/${submittedDocument.id!}/convert-pdf`,
          {
            method: "POST",
            headers: await useApiHeaders(),
            responseType: "blob",
            body: {
              lines: lines,
            },
          }
        );

        // Create a Blob from the response
        const blob = new Blob([response], { type: "application/pdf" });

        // Generate a URL from the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link and trigger the download
        const link = document.createElement("a");
        link.href = url;
        link.download = docName; // Suggest a filename for the download
        document.body.appendChild(link); // Append the link to the document
        link.click(); // Programmatically click the link to trigger the download

        // Clean up by revoking the Blob URL and removing the link
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        return;
      }

      if (isImage) {
        const imagePath = revision.fileObject.fullPath;

        const storage = useFirebaseStorage();
        const fileRef = firebaseRef(storage, imagePath);

        const imageBlob: any = await getBlob(fileRef);

        // Function to convert Blob to Base64
        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => {
              if (reader.result == undefined || reader.result == null) {
                reject(new Error("Failed to load image"));
                return;
              }

              // Remove `reader.result` prefix (e.g., 'data:image/jpeg;base64,') if any,
              // PDFMake requires the raw base64 string prefixed with 'data:image/jpeg;base64,'
              const base64String = (reader.result as string).replace(
                /^data:.+;base64,/,
                ""
              );
              resolve(base64String);
            };
            reader.readAsDataURL(blob);
          });

        // Use the function and await its result
        const base64String = await blobToBase64(imageBlob);

        // The image string for PDFMake
        const imageForPDFMake = `data:image/jpeg;base64,${base64String}`;

        const { width: originalWidth, height: originalHeight } =
          await useImageDimensions(imageBlob);

        const imageWidth = 500;

        content.push(
          ...[
            {
              image: imageForPDFMake,
              alignment: "center",
              width: imageWidth,
              height: Math.floor((originalHeight / originalWidth) * imageWidth),
            },
            { text: "\n" },
          ]
        );
      }
    }

    if (
      revision.commentObject &&
      revisionCommentPlacement == RevisionCommentPlacement.AfterDocument
    ) {
      // @ts-ignore
      // var lines = nuxtApp.$convertQuillDeltaToLines(revision.commentObject);
      const lines = useConvertQuillDeltaToPDFRun(
        revision.commentObject,
        12,
        useRGBToHex(commentColor)
      );

      content.push({
        text: "\n",
      });

      for (const line of lines) {
        content.push({
          text: line,
        });
      }
    }

    const docDefinition: any = {
      pageMargins: [60, 60, 60, 60],
      content: [...content],
    };

    const pdfMake = usePDFMake();

    // Define the fonts
    const fonts = {
      Tinos: {
        normal: window.location.origin + "/fonts/tinos/Tinos-Regular.ttf",
        bold: window.location.origin + "/fonts/tinos/Tinos-Bold.ttf",
        italics: window.location.origin + "/fonts/tinos/Tinos-Italic.ttf",
        bolditalics:
          window.location.origin + "/fonts/tinos/Tinos-BoldItalic.ttf",
      },
      Lato: {
        normal: window.location.origin + "/fonts/lato/Lato-Regular.ttf",
        bold: window.location.origin + "/fonts/lato/Lato-Bold.ttf",
        italics: window.location.origin + "/fonts/lato/Lato-Italic.ttf",
        bolditalics: window.location.origin + "/fonts/lato/Lato-BoldItalic.ttf",
      },
      Roboto: {
        // Add Arial or another font as a fallback
        normal: window.location.origin + "/fonts/roboto/Roboto-Regular.ttf",
        bold: window.location.origin + "/fonts/roboto/Roboto-Bold.ttf",
        italics: window.location.origin + "/fonts/roboto/Roboto-Italic.ttf",
        bolditalics:
          window.location.origin + "/fonts/roboto/Roboto-BoldItalic.ttf",
      },
    };

    // Ensure fonts are loaded before you use them
    pdfMake.fonts = fonts;

    docDefinition.defaultStyle = {
      font: "Tinos",
      fallbackFont: "Tinos",
    };

    if (returnBlob) {
      return new Promise((resolve, reject) => {
        pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
          resolve(blob);
        });
      });
    }

    if (printMode) {
      pdfMake.createPdf(docDefinition).print();
    } else {
      pdfMake.createPdf(docDefinition).download(docName);
    }
  };

  return { exportPDFDocument };
};
